import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AlertOctagon, AlertTriangle } from 'lucide-react';

import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils/Utils';

// Görev 10 — Erken Uyarılar sayfasının kanıt açılır paneli (spec §13.3). HaAlertsController/
// HaAlertsDataAccess'in (Görev 9) verdiği HaAlertResponse'u OLDUĞU GİBİ render eder; burada
// hiçbir hesaplama/toplama YAPILMAZ — kanıt zaten backend'de hazırlanmıştır (EvidenceJson).
//
// ═══ Neden burada isim/kimlik YOK ═══
// Bir satır ya bireyseldir (SubjectUserId dolu, EvidenceJson ham) ya toplanmıştır (SubjectUserId
// null, EvidenceJson yalnız {"affectedCount": N} taşır) — bkz. HaAlertsDataAccess sınıf özeti
// "fix round 1/2". Bu bileşen SubjectUserId'yi HİÇ okumaz/render etmez (ne bireysel ne toplu
// yolda) — kimlik kapısı zaten backend'de uygulanmış bir karardır, burada tekrarlanmaz/atlatılmaz.
// Toplu bir satırda EvidenceJson'ın KENDİSİ zaten affectedCount'tan başka hiçbir şey taşımaz
// (bkz. HaAlertsDataAccess.BuildMaskedGroupResponse) — yani bu bileşenin "eşik/kaynak" dalı o
// satırlar için hiç TETİKLENMEZ, icat edilen bir gizleme değil, backend'in kendi veri şeklidir.
//
// ═══ Kanıt alanları ALLOWLIST'ten geliyor ═══
// HaRuleCatalog.cs'in EvidenceSchema'sı (Number | Date | Bool | Token) EvidenceJson'da hangi
// anahtarların bulunabileceğini backend'de zaten sınırlar (serbest metin YASAK — Token yalnız
// HaEvidenceTokens'taki sabit kümeden gelir, ör. "WHO/ILO 2021", "rest_11h"). Burada o şemayı
// YENİDEN TANIMLAMIYORUZ, yalnızca bugünkü 8 kuralın BİLİNEN anahtarlarını okunur bir etikete
// çeviriyoruz (EVIDENCE_FIELD_ORDER) — tanınmayan bir anahtar gelirse (savunma amaçlı) ham
// anahtar adıyla yine de gösterilir, hiçbir alan sessizce YUTULMAZ.
export const SEVERITY_META = {
  red: { Icon: AlertOctagon, colorKey: 'danger', labelKey: 'hrAnalytics.alerts.severities.red' },
  amber: { Icon: AlertTriangle, colorKey: 'warning', labelKey: 'hrAnalytics.alerts.severities.amber' },
};

// [anahtar, çeviri anahtarı, tür]. tür: 'number' | 'date' | 'token'. Sıra bilinçli: kaynak/bulgu
// kodu ÖNCE (kanıtın neye dayandığı), sonra eşik/ölçülen değerler, sonra sayaçlar, sonra tarihler.
const EVIDENCE_FIELD_ORDER = [
  ['source', 'hrAnalytics.alerts.evidenceFields.source', 'token'],
  ['findingCode', 'hrAnalytics.alerts.evidenceFields.findingCode', 'token'],
  ['thresholdDays', 'hrAnalytics.alerts.evidenceFields.thresholdDays', 'number'],
  ['thresholdHours', 'hrAnalytics.alerts.evidenceFields.thresholdHours', 'number'],
  ['thresholdMinutes', 'hrAnalytics.alerts.evidenceFields.thresholdMinutes', 'number'],
  ['thresholdNights', 'hrAnalytics.alerts.evidenceFields.thresholdNights', 'number'],
  ['measuredHours', 'hrAnalytics.alerts.evidenceFields.measuredHours', 'number'],
  ['measuredMinutes', 'hrAnalytics.alerts.evidenceFields.measuredMinutes', 'number'],
  ['remainingDays', 'hrAnalytics.alerts.evidenceFields.remainingDays', 'number'],
  ['consecutiveDays', 'hrAnalytics.alerts.evidenceFields.consecutiveDays', 'number'],
  ['consecutiveNights', 'hrAnalytics.alerts.evidenceFields.consecutiveNights', 'number'],
  ['otherViolationCount', 'hrAnalytics.alerts.evidenceFields.otherViolationCount', 'number'],
  ['otherExpiringCount', 'hrAnalytics.alerts.evidenceFields.otherExpiringCount', 'number'],
  ['gapCount', 'hrAnalytics.alerts.evidenceFields.gapCount', 'number'],
  ['greenCount', 'hrAnalytics.alerts.evidenceFields.greenCount', 'number'],
  ['yellowCount', 'hrAnalytics.alerts.evidenceFields.yellowCount', 'number'],
  ['redCount', 'hrAnalytics.alerts.evidenceFields.redCount', 'number'],
  ['contractEndDate', 'hrAnalytics.alerts.evidenceFields.contractEndDate', 'date'],
  ['expirationDate', 'hrAnalytics.alerts.evidenceFields.expirationDate', 'date'],
];

const KNOWN_EVIDENCE_KEYS = new Set(EVIDENCE_FIELD_ORDER.map(([key]) => key));

/**
 * EvidenceJson'ı güvenle ayrıştırır — bozuk/eksik JSON'da HaAlert.EvidenceJson'ın kendi
 * "kanıt yok" varsayılanına ({}) düşer, asla fırlatmaz (FunnelPlot.js/SmartStatCard.js'teki AYNI
 * savunmacı ayrıştırma disiplini — çağırana güvenilmez, karar tüketim NOKTASINDA yeniden yapılır).
 */
export function parseAlertEvidence(evidenceJson) {
  if (!evidenceJson) return {};
  try {
    const parsed = JSON.parse(evidenceJson);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

// Sayılar burada BİLEREK Intl.NumberFormat ile gruplanmıyor (SmartStatCard/FunnelPlot'un aksine):
// bu değerler "büyük bir metrik" değil, tanısal bir eşik/ölçüm (ör. MinWeeklyRestMinutes=1440) —
// binlik ayırıcı locale'e göre değişip ("1.440" tr / "1,440" en) aynı sayıyı farklı okutabilir;
// kanıt panelinin amacı ham değeri BİREBİR göstermektir.
function formatFieldValue(evidence, key, kind, lang) {
  const value = evidence[key];
  if (kind === 'date') return formatDate(value, lang);
  return String(value);
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: flex-end;
  z-index: 1000;
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  width: 420px;
  max-width: 92vw;
  height: 100%;
  box-shadow: -8px 0 24px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.16);
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RuleCode = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  word-break: break-all;
`;

// Şiddet renk + ikon + metinle birlikte gösterilir (renk TEK BAŞINA değil) — FunnelPlot.js'in
// dosya başı yorumunda belgelenen ve incelemeden geçmiş AYNI ilke (dataviz kuralı).
const SeverityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $color }) => `${$color}22`};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const SummaryText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};

  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const FieldValue = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
  word-break: break-word;
`;

const Footer = styled.div`
  margin-top: auto;
  padding-top: 8px;
  display: flex;
  justify-content: flex-end;
`;

/**
 * Erken Uyarılar sayfasının kanıt açılır paneli. `alert` HaAlertResponse şeklindedir
 * (HrAnalyticsAlertsPage.js'in GET /v1/ha/alerts'ten aldığı satırın kendisi).
 *
 * @param {boolean} isOpen
 * @param {object|null} alert
 * @param {() => void} onClose
 */
export default function AlertEvidenceDrawer({ isOpen, alert, onClose }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const evidence = useMemo(() => parseAlertEvidence(alert?.evidenceJson), [alert]);

  if (!isOpen || !alert) return null;

  const severityMeta = SEVERITY_META[alert.severity] || SEVERITY_META.amber;
  const SeverityIcon = severityMeta.Icon;
  const severityColor = theme.colors.auxiliar[severityMeta.colorKey];

  // Toplu (maskelenmiş grup) satır: EvidenceJson SADECE affectedCount taşır (bkz. dosya başı
  // yorumu) — "4 çalışan" özetinin KENDİSİ zaten burada da tekrarlanır, isim/id YOK.
  const affectedCount = typeof evidence.affectedCount === 'number' ? evidence.affectedCount : null;

  const knownFields = EVIDENCE_FIELD_ORDER.filter(
    ([key]) => Object.prototype.hasOwnProperty.call(evidence, key)
  );
  const unknownFields = Object.keys(evidence).filter(
    (key) => key !== 'affectedCount' && !KNOWN_EVIDENCE_KEYS.has(key)
  );
  const hasFields = knownFields.length > 0 || unknownFields.length > 0;

  return (
    <Overlay onClick={onClose} data-role="alert-evidence-drawer">
      <Panel onClick={(e) => e.stopPropagation()}>
        <HeaderMeta>
          <Title>{t('hrAnalytics.alerts.evidence.title')}</Title>
          <RuleCode>{t('hrAnalytics.alerts.evidence.ruleLabel')}: {alert.ruleCode}</RuleCode>
          <SeverityBadge $color={severityColor}>
            <SeverityIcon size={14} aria-hidden="true" />
            {t(severityMeta.labelKey)}
          </SeverityBadge>
        </HeaderMeta>

        <MetaRow>
          <span>{t('hrAnalytics.alerts.columns.scope')}: {t(`hrAnalytics.scopes.${alert.scopeType}`)}</span>
          <span>{t('hrAnalytics.alerts.columns.triggeredAt')}: {formatDate(alert.triggeredAt, i18n.language)}</span>
          <span>{t('hrAnalytics.alerts.columns.status')}: {t(`hrAnalytics.alerts.statuses.${alert.status}`)}</span>
        </MetaRow>

        {affectedCount !== null ? (
          <SummaryText>
            {t('hrAnalytics.alerts.evidence.aggregatedSummary', { count: affectedCount })}
          </SummaryText>
        ) : hasFields ? (
          <FieldList>
            {knownFields.map(([key, labelKey, kind]) => (
              <FieldRow key={key}>
                <FieldLabel>{t(labelKey)}</FieldLabel>
                <FieldValue>{formatFieldValue(evidence, key, kind, i18n.language)}</FieldValue>
              </FieldRow>
            ))}
            {unknownFields.map((key) => (
              <FieldRow key={key}>
                <FieldLabel>{key}</FieldLabel>
                <FieldValue>{String(evidence[key])}</FieldValue>
              </FieldRow>
            ))}
          </FieldList>
        ) : (
          <SummaryText>{t('hrAnalytics.alerts.evidence.noEvidence')}</SummaryText>
        )}

        <Footer>
          <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
        </Footer>
      </Panel>
    </Overlay>
  );
}
